import { X } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Callout, Skeleton } from "@/components/ui/feedback";
import { ContractNotice, FieldControl } from "@/features/contact-fields";
import type { ReferenceChoice } from "@/features/reference-picker";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { NEEDED_SECTION, NEEDED_TITLE, type NeededRow, neededFields } from "@/lib/call-contract";
import {
  callFormSections, countedMarks, FORM_NOTICE_TEXT, formFolders, formNotice, saveSummary, sentOnChange,
  shownDraft, SAVED_SHOWN_MS, takesRow,
} from "@/lib/call-form";
import { leadName } from "@/lib/campaign";
import type { WorkLead } from "@/lib/phone-work";
import { useLeadCard, useLeadFormFields } from "@/lib/queries";
import { type Draft, editable, fieldText, ownFields } from "@/lib/record-fields";
import { type ContractAsk, contractProblem } from "@/lib/send-contract";
import type { LeadCard, RecordField, RecordSection } from "@/lib/types";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { ContractDetailAnswer, ContractFirmAnswer } from "@/softphone/call-contract";
import {
  type CallFormView, type FieldMark, commitCallFormField, dismissCallFormNotice, leaveCallForm, openCallForm,
  retryCallFormField, setCallFormDraft, useCallForm, useCallFormNotice,
} from "@/softphone/call-form-store";

/**
 * The form a rep fills in during a call: the contact's own fields in the call's three folders,
 * Contact, Required Intake Questions and Full Intake (`CALL_FORM_SECTIONS`, ACL-558), as far as the
 * rep's role may see them. Every other folder is the record's, on the contact's page. A pane of the
 * Phone page, beside the call's card, that the rep may put away and bring back from the phone's
 * rail (`softphone/call-panes.tsx`).
 *
 * There is no Save. A typed answer is sent when the rep leaves its field, a picked one when it is
 * picked, and the header says what happened. The answers being typed live in
 * `softphone/call-form-store.ts`, not here, so they are still in their inputs when the call ends
 * and this pane is drawn again for the outcome window, and they are sent even if the pane is gone.
 * Which lead the form is following is the page's to say (`useCallFormLead`), not the pane's: a rep
 * who puts the pane away in the middle of a call has not left the lead.
 *
 * The answers are a grid of as many columns as the pane has room for (ACL-605): short questions side
 * by side, read left to right and then down in the admin's order, so most of a folder is on screen at
 * once for the setter filling it in and the closer reading it after a transfer. It is the pane's own
 * width that decides, since the pane is wide alone and narrow beside the script or the texts. An
 * answer that needs the room, a long text or a list of ticks, has a row to itself (`takesRow`).
 * An input shows the rep's draft if there is one and the server's value otherwise, so the
 * half-minute refetch and another field's save never move what is being typed.
 *
 * A field the role may only read is its question over its answer as text, not a greyed input: on a
 * call a rep reads the answer, and an input they cannot type in is one more thing to tab through.
 *
 * `live` false means a call took place with this lead and nothing names them any more: the page
 * keeps no lead that was only offered or dialed (`pageLead`), so the line under the title can say
 * the call has ended without ever saying it of somebody who was not called.
 *
 * `ask` is what Send contract on the call's card listed to fix, handed over when its dialog closes
 * (ACL-560): until then the form reads as it always does. Each value it names is outlined in the
 * warning tone with why under it, where the call's folders have it; the ones they do not hold (the
 * e-mail, the name, a minor's guardian, the firm) are a folder of their own at the top, "Needed for the
 * contract", which leads the header's links. The first is scrolled to, and each mark goes as soon as
 * its value is saved; the folder stays, with what was filled in, until the next press.
 */
export function CallFormPane({
  lead, live, onClose, className, ask = null,
}: {
  /** The lead whose form this is, or null on a campaign call whose caller has no contact yet. */
  lead: WorkLead | null;
  /** A call, a dial or the outcome window still names the lead; false once it is only kept. */
  live: boolean;
  onClose: () => void;
  className?: string;
  /** What Send contract on the call asked to fix for this lead (`callContractAsk`). */
  ask?: ContractAsk | null;
}) {
  const leadId = lead?.id ?? null;
  const card = useLeadCard(leadId, lead?.stay ?? null).data;
  const name = card ? (leadName(card) ?? formatPhoneNumber(card.phone)) : null;
  const view = useCallForm(leadId);
  const { user } = useAuth();
  // The same read the body draws from (one cache entry), for the header's folders and its count.
  const sections = useLeadFormFields(leadId).data?.sections;
  const needed = neededFields(leadId === null ? null : ask, sections, user?.role);
  const folders = [
    ...(needed.length > 0 ? [{ key: NEEDED_SECTION, title: NEEDED_TITLE }] : []),
    ...formFolders({ hasLead: leadId !== null, sections }),
  ];

  return (
    <section aria-label="Form" className={cn("flex min-h-0 flex-col", className)}>
      {/* The header is the form's folders: each name takes the rep down to that folder's answers,
          and the row stays put while the answers scroll under it. It was a title, "Form" and the
          lead's name, over a second row with these links: the card beside the pane already says
          who, and the folders say what the pane is. Until there are folders to name (the first
          read, a caller with no contact, a form nobody set up) it still says "Form". */}
      <header className="shrink-0 border-b border-border px-4 py-2.5">
        <div className="flex min-h-7 items-center gap-2">
          {folders.length > 0 ? (
            <>
              <h2 className="sr-only">Form</h2>
              <nav aria-label="Folders" className="flex min-w-0 flex-wrap gap-x-4 gap-y-1">
                {folders.map((folder) => (
                  <button
                    key={folder.key}
                    type="button"
                    className="text-sm font-medium text-accent-11 underline-offset-4 hover:underline"
                    onClick={() => document.getElementById(folderId(folder.key))?.scrollIntoView({ block: "start" })}
                  >
                    {folder.title}
                  </button>
                ))}
              </nav>
            </>
          ) : (
            <>
              <h2 className="text-sm font-semibold text-text">Form</h2>
              {name && <span className="min-w-0 truncate text-sm text-text-muted">{name}</span>}
            </>
          )}
          <SaveState view={view} sections={sections} also={needed.flatMap((row) => (row.kind === "answer" ? [row.key] : []))} />
          {!live && (
            <Button variant="ghost" size="icon-sm" aria-label="Close the form" onClick={onClose}>
              <X />
            </Button>
          )}
        </div>
        {!live && (
          <p className="text-xs text-text-muted">The call has ended. You can still finish the form.</p>
        )}
      </header>
      {/* One block under the header, so the pane is a header and a body whatever is in the body:
          the Phone page sets the two on the rows it shares with the pane beside this one. */}
      <div className="flex min-h-0 flex-1 flex-col">
        <CallFormLeftNotice className="mx-4 mt-3 shrink-0" />
        <div className="@container/form relative min-h-0 flex-1 overflow-y-auto scrollbar-subtle p-4">
          {leadId === null ? (
            <Line>{FORM_NOTICE_TEXT.no_contact}</Line>
          ) : (
            <FormBody key={leadId} leadId={leadId} view={view} ask={ask} needed={needed} card={card ?? null} />
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Follows the lead the page works with, whether or not the form's pane is on screen. Leaving a
 * lead (another lead, Close, a ring that takes the page, the page gone) sends what can be sent
 * and says what could not; putting the pane away does neither, since the lead is still the rep's
 * and the answers are in the store for when the pane is back. Called once, by the page.
 */
export function useCallFormLead(lead: WorkLead | null): void {
  const leadId = lead?.id ?? null;
  const card = useLeadCard(leadId, lead?.stay ?? null).data;
  const name = card ? (leadName(card) ?? formatPhoneNumber(card.phone)) : null;
  // The name is read from a ref written after this effect, so the cleanup for the lead that went
  // still has that lead's name.
  const shownName = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (leadId === null) return;
    openCallForm(leadId);
    return () => leaveCallForm(leadId, shownName.current);
  }, [leadId]);
  React.useEffect(() => {
    shownName.current = name;
  }, [name]);
}

/**
 * The warning for answers left unsaved on a lead the rep is no longer on, whatever the page shows
 * now. The pane draws it at its top; the page draws it itself while there is no pane (after Close,
 * between calls with nothing kept, on a direct call), because that is exactly when a rep has just
 * left a lead and would otherwise never be told. One of the two, never both. The two actions have
 * a row of their own, so the length of the sentence does not decide where they break.
 */
export function CallFormLeftNotice({ className }: { className?: string }) {
  const left = useCallFormNotice();
  if (!left) return null;
  return (
    <Callout tone="warning" className={className}>
      <p>{left.text}</p>
      <p className="mt-1 flex gap-3">
        <a
          href={`/contacts/${left.leadId}`}
          target="_blank"
          rel="noreferrer"
          className="font-medium underline-offset-4 hover:underline"
        >
          Open record
        </a>
        <button
          type="button"
          className="font-medium underline-offset-4 hover:underline"
          onClick={dismissCallFormNotice}
        >
          Dismiss
        </button>
      </p>
    </Callout>
  );
}

function Line({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-text-muted">{children}</p>;
}

/** The header's words about saving. "Saved" goes by itself two seconds after the last save. Only
 *  the answers the form on screen still has an input for are counted as not saved: its folders', and
 *  `also`, the answers "Needed for the contract" draws. */
function SaveState({
  view, sections, also,
}: {
  view: CallFormView;
  sections: readonly RecordSection[] | undefined;
  also: readonly string[];
}) {
  const [, tick] = React.useState(0);
  const { savedAt } = view;
  React.useEffect(() => {
    if (savedAt === null) return;
    const left = savedAt + SAVED_SHOWN_MS - Date.now();
    if (left <= 0) return;
    const timer = setTimeout(() => tick((n) => n + 1), left);
    return () => clearTimeout(timer);
  }, [savedAt]);
  const summary = saveSummary({
    saving: view.saving,
    failed: countedMarks(view.marks, sections, also),
    savedAgoMs: savedAt === null ? null : Date.now() - savedAt,
  });
  return (
    <span
      aria-live="polite"
      className={cn(
        "ml-auto shrink-0 text-xs",
        summary?.tone === "danger" ? "font-medium text-danger-11" : "text-text-muted",
      )}
    >
      {summary?.text}
    </span>
  );
}

const folderId = (key: string) => `call-form-folder-${key}`;

/** A sentence over the answers, not as wide as the pane: the answers have all of it, a line of words
 *  does not read across it. */
const MEASURE = "max-w-3xl";

/**
 * The answers' grid (ACL-605): as many columns as the pane's width holds, so a wide pane has more and
 * a pane beside the script fewer, and nothing is ever cut off or scrolled sideways. A column is 13rem
 * at the least, where a short question and its input read on a line; in a pane too narrow for two of
 * those (a laptop's, beside the script or the texts) it gives way down to 11rem, so the answers are
 * still two side by side and not one long list. `auto-fill` keeps the empty columns of a short folder,
 * so every folder's columns are the same and line up one under another. Never `dense`: the admin's
 * order is read left to right, then down, and a later answer must not fill an earlier gap.
 *
 * No row gap: a gap would also lie between the four rows each answer takes (`CELL`), and under an
 * answer with no line under it and no Try again it came to three gaps. Each answer's own margin is
 * the room under it instead.
 */
const GRID = "grid grid-cols-[repeat(auto-fill,minmax(clamp(11rem,calc(50%_-_0.5rem),13rem),1fr))] gap-x-4";

/**
 * One answer in `GRID`: four of its rows, the question, the input, the line under it and a Try again,
 * so the inputs of a row start level however many lines the questions beside them take. A cell that
 * draws its own parts on them is a subgrid as well (`SUBGRID`). `mb-3` is the room before the next
 * row of answers.
 */
const CELL = "row-span-4 mb-3 min-w-0";
const SUBGRID = "grid grid-rows-subgrid";

/**
 * Where an answer goes in `GRID`, given the one before it. A long text has a row to itself but is two
 * columns wide, so its box ends on a column's edge like the inputs over and under it rather than
 * running across a wide pane (the pane's width where `GRID` has one column, which is exactly below
 * 23rem: two 11rem columns and their gap). A list of ticks has the whole row, across which its options
 * lie. The answer after either starts the next row, so nothing sits beside them. `field` is missing for
 * a cell of "Needed for the contract" that is not a field of the form (the firm, a value elsewhere).
 */
function placed(field: RecordField | undefined, after: RecordField | undefined): string | undefined {
  if (field === undefined) return after && takesRow(after) ? "col-start-1" : undefined;
  // One `grid-column` for both: a `col-span-2` of its own would reset a `col-start-1` set beside it.
  if (field.type === "long_text") return "col-span-full @min-[23rem]/form:col-[1/span_2]";
  if (takesRow(field)) return "col-span-full";
  return after && takesRow(after) ? "col-start-1" : undefined;
}

/** A value Send contract asked for, outlined in the warning tone (ACL-541's), standing a little out
 *  from the answers beside it so the text does not move: over it by its padding, and under it into the
 *  room `CELL` leaves, by less than half of it, so two marked one over the other do not touch. */
const MARKED = "-mx-2 -mt-1 mb-2 rounded-md border border-warning-9 bg-warning-2 px-2 py-1";

function FormBody({
  leadId, view, ask, needed, card,
}: {
  leadId: string;
  view: CallFormView;
  ask: ContractAsk | null;
  needed: readonly NeededRow[];
  card: LeadCard | null;
}) {
  const fields = useLeadFormFields(leadId);
  const notice = formNotice({
    hasLead: true,
    sections: fields.data?.sections,
    failed: fields.error !== null,
    status: fields.error instanceof ApiError ? fields.error.status : null,
  });
  // A new ask scrolls to the first value it marks, once the form is there to mark it (ACL-560).
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const scrolledFor = React.useRef<ContractAsk | null>(null);
  const loaded = fields.data !== undefined;
  React.useEffect(() => {
    if (!ask || !loaded || scrolledFor.current === ask) return;
    const first = bodyRef.current?.querySelector("[data-contract-problem]");
    if (!first) return;
    scrolledFor.current = ask;
    first.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [ask, loaded]);

  if (notice === "loading") {
    return (
      <div className="space-y-3" aria-busy>
        <span className="sr-only">Loading…</span>
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
      </div>
    );
  }
  if (notice === "failed") {
    return (
      <div className="space-y-2">
        <Line>{FORM_NOTICE_TEXT.failed}</Line>
        <Button variant="outline" size="sm" onClick={() => void fields.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  const sections = callFormSections(fields.data?.sections ?? []);
  return (
    <div ref={bodyRef} className="space-y-3">
      {needed.length > 0 && <NeededFolder leadId={leadId} rows={needed} view={view} card={card} />}
      {notice !== null && notice !== "read_only" && <Line>{FORM_NOTICE_TEXT[notice]}</Line>}
      {notice === "read_only" && <Callout tone="info" className={MEASURE}>{FORM_NOTICE_TEXT.read_only}</Callout>}
      {(notice === null || notice === "read_only") && sections.map((section) => (
        <section key={section.key} aria-labelledby={folderId(section.key)}>
          {/* The margin is the body's own padding: a folder the header's link scrolls to stops
              where the first folder starts, not against the header's line. */}
          <h3 id={folderId(section.key)} className="scroll-mt-4 text-sm font-semibold text-text">
            {section.title}
          </h3>
          <div className={cn("mt-2", GRID)}>
            {ownFields(section).map((field, i, fields) =>
              editable(field) ? (
                <Answer
                  key={field.key}
                  leadId={leadId}
                  field={field}
                  after={fields[i - 1]}
                  draft={view.drafts[field.key]}
                  mark={view.marks[field.key]}
                  contract={contractProblem(ask?.get(field.key), fieldText(field))}
                />
              ) : (
                <ReadAnswer key={field.key} field={field} className={cn(CELL, SUBGRID, placed(field, fields[i - 1]))} />
              ),
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * An answer the role may only read: its question over its answer, where an input would be, so a
 * closer's form after a transfer is in the same columns as the setter's. It was a row of a full-width
 * list, the question to the left, and every one of them took a line of the pane however short.
 */
function ReadAnswer({ field, className }: { field: RecordField; className?: string }) {
  const text = fieldText(field);
  return (
    <dl className={className}>
      {/* Spaced from the answer as a question is from its input (`Field`). */}
      <dt className="mb-1.5 text-xs font-medium text-text-muted">{field.title}</dt>
      <dd className="min-w-0 break-words text-sm text-text">
        {text === null ? <span className="text-text-muted">—</span> : (
          <span className={field.type === "long_text" ? "whitespace-pre-wrap" : field.type === "email" ? "break-all" : undefined}>
            {text}
          </span>
        )}
      </dd>
    </dl>
  );
}

function Answer({
  leadId, field, after, draft, mark, contract = null,
}: {
  leadId: string;
  field: RecordField;
  /** The answer before this one in its folder, which decides where this one goes (`placed`). */
  after?: RecordField;
  draft: Draft | undefined;
  mark: FieldMark | undefined;
  /** Why Send contract needs this answer, while it still does (ACL-560): outlined, said under it. */
  contract?: string | null;
}) {
  const atOnce = sentOnChange(field);
  const shown = shownDraft(field, draft);
  // The record picked here, for a reference: the draft holds only its id, so the name is kept beside
  // it and shown for as long as that id is still the answer.
  const [picked, setPicked] = React.useState<ReferenceChoice | null>(null);
  return (
    <div
      // The grid's four rows: the field lies on the first three (question, input, the line under it)
      // and Try again, when there is one, on the last.
      className={cn(CELL, SUBGRID, placed(field, after), contract && MARKED)}
      data-contract-problem={contract ? "" : undefined}
      // Only what is typed waits for the rep to leave it. A Select's trigger loses focus to its own
      // list when it opens, which is not leaving the answer, so a picked answer has no blur at all.
      onBlur={
        atOnce
          ? undefined
          : (e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) commitCallFormField(leadId, field);
            }
      }
    >
      <FieldControl
        idPrefix="call-form"
        entity="lead"
        className={cn("row-span-3", SUBGRID)}
        field={field}
        draft={shown}
        pickedLabel={picked && String(picked.id) === shown ? picked.label : undefined}
        problem={mark?.text}
        notice={contract}
        onChange={(next) => {
          setCallFormDraft(leadId, field, next);
          if (atOnce) commitCallFormField(leadId, field);
        }}
        onPick={(choice) => {
          setPicked(choice);
          setCallFormDraft(leadId, field, choice ? String(choice.id) : "");
          commitCallFormField(leadId, field);
        }}
      />
      {mark?.kind === "failed" && (
        <button
          type="button"
          className="mt-1 justify-self-start text-xs font-medium text-accent-11 underline-offset-4 hover:underline"
          onClick={() => retryCallFormField(leadId, field)}
        >
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * "Needed for the contract" (ACL-560): what Send contract on the call asked for that the call's folders
 * have no input for, at the top of the form, in the order the dialog listed it. Each row fills its value
 * in the way the value is saved (`neededFields`): a form answer through the call's queue, one of the
 * contact's own details and the firm through the contact. A value this role may only read, or one their
 * form does not hold, is said with why it is needed, and the record is a link away for it.
 */
function NeededFolder({
  leadId, rows, view, card,
}: {
  leadId: string;
  rows: readonly NeededRow[];
  view: CallFormView;
  card: LeadCard | null;
}) {
  return (
    <section aria-labelledby={folderId(NEEDED_SECTION)}>
      <h3 id={folderId(NEEDED_SECTION)} className="scroll-mt-4 text-sm font-semibold text-text">
        {NEEDED_TITLE}
      </h3>
      <p className="text-xs text-text-muted">Send contract needs these, and the folders below do not hold them.</p>
      <div className={cn("mt-3", GRID)}>
        {rows.map((row, i) => {
          const before = rows[i - 1];
          const after = before && "field" in before ? before.field : undefined;
          switch (row.kind) {
            case "answer":
              return (
                <Answer
                  key={row.key}
                  leadId={leadId}
                  field={row.field}
                  after={after}
                  draft={view.drafts[row.key]}
                  mark={view.marks[row.key]}
                  contract={contractProblem(row.problem, fieldText(row.field))}
                />
              );
            case "detail": {
              const says = contractProblem(row.problem, fieldText(row.field));
              return (
                <Needed key={row.key} says={says} className={placed(row.field, after)}>
                  <ContractDetailAnswer leadId={leadId} field={row.field} notice={says} />
                </Needed>
              );
            }
            case "firm": {
              if (card === null) return null;
              const says = contractProblem(row.problem, card.firm?.label ?? null);
              return (
                <Needed key={row.key} says={says} className={placed(undefined, after)}>
                  <ContractFirmAnswer card={card} notice={says} />
                </Needed>
              );
            }
            case "read": {
              const says = contractProblem(row.problem, fieldText(row.field));
              return (
                <Needed key={row.key} says={says} className={placed(row.field, after)}>
                  <ReadAnswer field={row.field} />
                  {says && <NotHere says={says} leadId={leadId} why="Your role can read it here, not change it." />}
                </Needed>
              );
            }
            case "elsewhere":
              return (
                <Needed key={row.key} says={row.problem.message} className={placed(undefined, after)}>
                  <NotHere says={row.problem.message} leadId={leadId} why="It is not on the form you can see." />
                </Needed>
              );
          }
        })}
      </div>
    </section>
  );
}

/** One cell of "Needed for the contract", outlined while Send contract still needs its value. Its
 *  parts are other screens' (the contact's details, the firm), so it lies across the grid's four rows
 *  as one block rather than on them. */
function Needed({
  says, className, children,
}: {
  says: string | null;
  /** Where it goes in the grid (`placed`). */
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(CELL, className, says && MARKED)} data-contract-problem={says ? "" : undefined}>
      {children}
    </div>
  );
}

/** A value the call's form cannot fill in: why the contract needs it, and the record, in a new tab so
 *  the call's page stays where it is. */
function NotHere({ says, leadId, why }: { says: string; leadId: string; why: string }) {
  return (
    <p className="pt-1 text-xs">
      <ContractNotice text={says} /> <span className="text-text-muted">{why}</span>{" "}
      <a
        href={`/contacts/${leadId}`}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-accent-11 underline-offset-4 hover:underline"
      >
        Open record
      </a>
    </p>
  );
}
